import { createContext, useContext, useState, useCallback } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState({
    isOpen: false,
    title: "",
    description: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    destructive: true,
    resolve: null,
  });

  const confirm = useCallback(
    ({
      title = "Are you sure?",
      description = "",
      confirmText = "Confirm",
      cancelText = "Cancel",
      destructive = true,
    } = {}) => {
      return new Promise((resolve) => {
        setDialog({
          isOpen: true,
          title,
          description,
          confirmText,
          cancelText,
          destructive,
          resolve,
        });
      });
    },
    []
  );

  const handleConfirm = () => {
    if (dialog.resolve) dialog.resolve(true);
    setDialog((prev) => ({ ...prev, isOpen: false, resolve: null }));
  };

  const handleCancel = () => {
    if (dialog.resolve) dialog.resolve(false);
    setDialog((prev) => ({ ...prev, isOpen: false, resolve: null }));
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <AlertDialog
        open={dialog.isOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
      >
        <AlertDialogContent className="max-w-md rounded-2xl border border-border bg-surface p-6 shadow-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-lg font-bold text-foreground">
              {dialog.title}
            </AlertDialogTitle>
            {dialog.description && (
              <AlertDialogDescription className="text-xs text-muted-foreground leading-relaxed">
                {dialog.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-5 flex gap-2">
            <AlertDialogCancel asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="rounded-xl text-xs"
              >
                {dialog.cancelText}
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                size="sm"
                onClick={handleConfirm}
                className={`rounded-xl text-xs font-semibold ${
                  dialog.destructive
                    ? "bg-destructive text-white hover:bg-destructive/90"
                    : "bg-accent text-white hover:bg-accent-hover"
                }`}
              >
                {dialog.confirmText}
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used within a ConfirmProvider");
  }
  return ctx;
};
